import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  getGenericResearchIssuesDetailPaginated,
  getGenericResearchIssuesDetailPaginatedWithoutIssues,
  IResearchIssuesParams,
} from "../services/ResearchIssuesService";

export interface IResearchIssuesData {
  items: any[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

interface IUseFetchGenericResearchIssuesProps {
  executionId: number | null;
  queryName?: string;
  page: number;
  pageSize: number;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  additionalFilters?: Record<string, any>;
  enabled?: boolean; // To control when the hook should fetch data
  isIssuesDataAvailable: boolean;
}

/**
 * Custom hook to fetch research issues with pagination and filtering
 * @param props - Hook configuration including executionId, pagination, filters, etc.
 * @returns Research issues data and refetch function
 */
const useFetchGenericResearchIssues = ({
  executionId,
  queryName,
  page,
  pageSize,
  setIsLoading,
  additionalFilters = {},
  enabled = true,
  isIssuesDataAvailable,
}: IUseFetchGenericResearchIssuesProps): [IResearchIssuesData, () => void] => {
  const [researchIssuesData, setResearchIssuesData] =
    useState<IResearchIssuesData>({
      items: [],
      total: 0,
      page: 1,
      size: 25,
      pages: 0,
    });

  // Use ref to track if we're currently fetching to prevent multiple simultaneous calls
  const isFetchingRef = useRef(false);

  // Memoize additionalFilters to prevent infinite re-renders
  const memoizedAdditionalFilters = useMemo(
    () => additionalFilters,
    [JSON.stringify(additionalFilters)]
  );

  const fetchResearchIssues = useCallback(async () => {
    if (!executionId || !enabled || isFetchingRef.current) {
      if (!executionId || !enabled) {
        setResearchIssuesData({
          items: [],
          total: 0,
          page: 1,
          size: 25,
          pages: 0,
        });
      }
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const params: IResearchIssuesParams = {
        execution_id: executionId,
        page,
        size: pageSize,
        ...memoizedAdditionalFilters,
      };

      // Add query_name if provided
      if (queryName) {
        params.query_name = queryName.trim();
      }
      let result;
      if (isIssuesDataAvailable) {
        result = await getGenericResearchIssuesDetailPaginated(params);
      } else {
        result = await getGenericResearchIssuesDetailPaginatedWithoutIssues(
          params
        );
      }
      setResearchIssuesData(
        result || {
          items: [],
          total: 0,
          page: 1,
          size: 25,
          pages: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching research issues:", error);
      setResearchIssuesData({
        items: [],
        total: 0,
        page: 1,
        size: 25,
        pages: 0,
      });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    executionId,
    queryName,
    page,
    pageSize,
    memoizedAdditionalFilters,
    enabled,
  ]);

  useEffect(() => {
    fetchResearchIssues();
  }, [fetchResearchIssues]);

  // Return data and refetch function
  return [researchIssuesData, fetchResearchIssues];
};

export default useFetchGenericResearchIssues;
